import { Injectable } from '@angular/core';
import moment from 'moment';

import { BookingItem } from '@fry/booking/lib/booking-items';

import { EditorBookingItem } from '../editor-booking-item/editor-booking-item.model';
import {
  EditorBookingItemMetadata
} from '../editor-booking-item-metadata/editor-booking-item-metadata.model';
import {
  EditorBookingItemSetting
} from '../editor-booking-item-setting/editor-booking-item-setting.model';
import {
  EditorBookingItemSettingWorkflow
} from '../editor-booking-item-setting/editor-booking-item-setting-workflow.model';
import {
  EditorBookingItemSettingBookingDate
} from '../editor-booking-item-setting/editor-booking-item-setting-booking-date.model';
import {
  EditorBookingItemSettingBookingWindow
} from '../editor-booking-item-setting/editor-booking-item-setting-booking-window.model';
import {
  EditorBookingItemSettingCapacity
} from '../editor-booking-item-setting/editor-booking-item-setting-capacity.model';
import {
  EditorBookingItemSettingEligibility
} from '../editor-booking-item-setting/editor-booking-item-setting-eligibility.model';
import {
  EditorBookingItemSettingPricing
} from '../editor-booking-item-setting/editor-booking-item-setting-pricing.model';
import {
  EditorBookingItemSettingVenue
} from '../editor-booking-item-setting/editor-booking-item-setting-venue.model';
import { EditorBookingItemTree } from './editor-booking-item-tree.model';


@Injectable()
export class EditorBookingItemTreeService {
  constructor() {

  }

  getEmptyItemTree(bookingItem: BookingItem): EditorBookingItemTree {
    const itemList: EditorBookingItem[] = [];

    const newSettings = [
      new EditorBookingItemSettingBookingDate({
        inheritableType: 'empty'
      }),
      new EditorBookingItemSettingPricing({
        inheritableType: 'empty'
      }),
      new EditorBookingItemSettingCapacity({
        inheritableType: 'empty'
      }),
      new EditorBookingItemSettingVenue({
        inheritableType: 'empty'
      }),
      new EditorBookingItemSettingBookingWindow({
        inheritableType: 'empty'
      }),
      new EditorBookingItemSettingWorkflow({
        inheritableType: 'empty'
      }),
      new EditorBookingItemSettingEligibility({
        inheritableType: 'empty'
      })
    ];

    itemList.push(
      new EditorBookingItem({
        id: bookingItem.id,
        name: 'New Bookable Item',
        canBeBooked: false,
        hideSpecificBookingSettings: false,
        bundle: [],
        settings: newSettings,
        metadataList: [],
        showSettings: true
      })
    );

    const itemTree = new EditorBookingItemTree({
      state: $localize `Draft`,
      bookingType: null,
      examType: null,
      risrAssessConnectionId: null,
      risrAssessBlueprintId: null,
      risrAssessItemSetGenerationMethod: null,
      risrAssessItemSetNumber: null,
      extraRoles: [],
      itemList
    });

    itemTree.itemList.forEach(item => itemTree.resetChildren(item.id));
    itemTree.itemList.forEach(item => itemTree.resetSiblingsForAllChildren(item.id));

    return itemTree;
  }

  getItemTreeFromBookingItem(bookingItem: BookingItem): EditorBookingItemTree {
    const itemList: EditorBookingItem[] = [];

    const newSettings = this.parseSettings(bookingItem.doc.session);
    const newMetadataList = this.parseMetadataList(bookingItem.doc.session);
    const newItem = new EditorBookingItem({
      id: bookingItem.id,
      name: bookingItem.doc.name,
      canBeBooked: bookingItem.doc.session.allowBooking,
      hideSpecificBookingSettings: bookingItem.doc.session.hideTitles === null ? false : bookingItem.doc.session.hideTitles,
      bundle: [],
      settings: newSettings,
      metadataList: newMetadataList,
      showSettings: true
    });

    itemList.push(newItem);

    itemList.push(
      ...this.parseSessions(bookingItem.doc.session.sessions, newItem)
    );

    const itemTree = new EditorBookingItemTree({
      bookingType: bookingItem.doc.bookingType,
      examType: bookingItem.doc.examType,
      risrAssessConnectionId: bookingItem.doc.risrAssess?.connectionId,
      risrAssessBlueprintId: bookingItem.doc.risrAssess?.blueprintId,
      risrAssessItemSetGenerationMethod: bookingItem.doc.risrAssess?.itemSetGenerationMethod,
      risrAssessItemSetNumber: bookingItem.doc.risrAssess?.itemSetNumber,
      extraRoles: bookingItem.doc.securityContext === undefined || bookingItem.doc.securityContext.roles === null
        ? []
        : [...bookingItem.doc.securityContext.roles],
      itemList
    });

    itemTree.itemList.forEach(item => itemTree.resetChildren(item.id));
    itemTree.itemList.forEach(item => itemTree.resetSiblingsForAllChildren(item.id));

    itemTree.itemList.forEach(item => {
      if (item.parentId !== null) {
        const parentItem = itemTree.getItemById(item.parentId);
        item.ancestorCanBeBooked = (parentItem === null) ? false : parentItem.canBeBooked || parentItem.ancestorCanBeBooked;
        item.settings = parentItem.getCompleteSettings(item.settings);
        item.metadataList = parentItem.getCompleteMetadataList(item.metadataList);
        item.isLastSibling = parentItem.children[parentItem.children.length - 1] === item.id;
      }
    });

    return itemTree;
  }

  private parseSessions(sessions: any, parentItem: EditorBookingItem): EditorBookingItem[] {
    const itemList: EditorBookingItem[] = [];

    if (sessions !== undefined) {
      sessions.forEach(session => {
        const newItem = this.parseSession(session, parentItem);
        itemList.push(newItem);

        if (session.sessions !== undefined) {
          itemList.push(...this.parseSessions(session.sessions, newItem));
        }
      });
    }

    return itemList;
  }

  private parseSession(session: any, parentItem: EditorBookingItem): EditorBookingItem {
    const newSettings = this.parseSettings(session);
    const newMetadataList = this.parseMetadataList(session);
    return new EditorBookingItem({
      id: session.id,
      parentId: parentItem.id,
      name: session.title,
      canBeBooked: session.allowBooking,
      hideSpecificBookingSettings: session.hideTitles === null ? false : session.hideTitles,
      level: parentItem.level + 1,
      bundle: session.bundle === undefined || session.bundle.options === undefined ? [] : [...session.bundle.options],
      settings: newSettings,
      metadataList: newMetadataList
    });
  }

  private parseSettings(session: any): EditorBookingItemSetting[] {
    return [
      this.parseBookingDate(session),
      this.parsePricing(session),
      this.parseCapacity(session),
      this.parseVenue(session),
      this.parseBookingWindow(session),
      this.parseWorkflow(session),
      this.parseEligibility(session)
    ];
  }

  private parseMetadataList(session: any): EditorBookingItemMetadata[] {
    if (session.metadata === undefined) {
      return [];
    } else {
      return session.metadata.map(metadata => new EditorBookingItemMetadata({
        name: metadata.key,
        value: metadata.value,
        inheritableType: 'set'
      }));
    }
  }

  private parseBookingDate(session: any): EditorBookingItemSettingBookingDate {
    if (session.bookingDates === undefined) {
      return new EditorBookingItemSettingBookingDate({
        inheritableType: 'empty'
      });
    } else {
      // TODO: timezone should be always present, this has to be removed
      if (session.bookingDates.timezone === undefined || session.bookingDates.timezone === null) {
        session.bookingDates.timezone = 'Europe/London';
      }
      return new EditorBookingItemSettingBookingDate({
        start: session.bookingDates.startDate === null
          ? null
          : moment(moment(session.bookingDates.startDate).tz(session.bookingDates.timezone).format('YYYY-MM-DD HH:mm')),
        end: session.bookingDates.endDate === null
          ? null
          : moment(moment(session.bookingDates.endDate).tz(session.bookingDates.timezone).format('YYYY-MM-DD HH:mm')),
        timezone: session.bookingDates.timezone,
        inheritableType: 'set'
      });
    }
  }

  private parseBookingWindow(session: any): EditorBookingItemSettingBookingWindow {
    if (session.bookingWindow === undefined) {
      return new EditorBookingItemSettingBookingWindow({
        inheritableType: 'empty'
      });
    } else {
      // TODO: timezone should be always present, this has to be removed
      if (session.bookingWindow.timezone === undefined || session.bookingWindow.timezone === null) {
        session.bookingWindow.timezone = 'Europe/London';
      }
      return new EditorBookingItemSettingBookingWindow({
        open: session.bookingWindow.startDate === null
          ? null
          : moment(moment(session.bookingWindow.startDate).tz(session.bookingWindow.timezone).format('YYYY-MM-DD HH:mm')),
        close: session.bookingWindow.endDate === null
          ? null
          : moment(moment(session.bookingWindow.endDate).tz(session.bookingWindow.timezone).format('YYYY-MM-DD HH:mm')),
        timezone: session.bookingWindow.timezone,
        inheritableType: 'set'
      });
    }
  }

  private parseCapacity(session: any): EditorBookingItemSettingCapacity {
    if (session.capacities === undefined) {
      return new EditorBookingItemSettingCapacity({
        inheritableType: 'empty'
      });
    } else {
      return new EditorBookingItemSettingCapacity({
        available: session.capacities.availableCapacity,
        inheritableType: 'set'
      });
    }
  }

  private parseEligibility(session: any): EditorBookingItemSettingEligibility {
    if (session.eligibilityId !== undefined && session.eligibilityId !== null) {
      return new EditorBookingItemSettingEligibility({
        id: session.eligibilityId,
        inheritableType: 'set'
      });
    }

    return new EditorBookingItemSettingEligibility({
      inheritableType: 'empty'
    });
  }

  private parsePricing(session: any): EditorBookingItemSettingPricing {
    if (session.pricing === undefined) {
      return new EditorBookingItemSettingPricing({
        inheritableType: 'empty'
      });
    } else {
      return new EditorBookingItemSettingPricing({
        price: session.pricing.price,
        currency: session.pricing.currency,
        methods: session.pricing.method,
        inheritableType: 'set'
      });
    }
  }

  private parseVenue(session: any): EditorBookingItemSettingVenue {
    if (session.venue === undefined) {
      return new EditorBookingItemSettingVenue({
        inheritableType: 'empty'
      });
    } else {
      return new EditorBookingItemSettingVenue({
        id: session.venue.venue,
        inheritableType: 'set'
      });
    }
  }

  private parseWorkflow(session: any): EditorBookingItemSettingWorkflow {
    if (session.workflowId !== undefined && session.workflowId !== null) {
      return new EditorBookingItemSettingWorkflow({
        id: session.workflowId,
        inheritableType: 'set'
      });
    } else {
      return new EditorBookingItemSettingWorkflow({
        inheritableType: 'empty'
      });
    }
  }

  getBookingItemFromItemTree(bookingItem: BookingItem, itemTree: EditorBookingItemTree): BookingItem {
    return Object.assign(
      new BookingItem(bookingItem.doc),
      bookingItem,
      {
        _doc: {
          ...bookingItem.doc, // TODO: check how to override _doc
          bookingType: itemTree.bookingType,
          examType: itemTree.examType,
          risrAssess: {
            connectionId: itemTree.risrAssessConnectionId,
            blueprintId: itemTree.risrAssessBlueprintId,
            itemSetGenerationMethod: itemTree.risrAssessItemSetGenerationMethod,
            itemSetNumber: itemTree.risrAssessItemSetNumber,
          },
          name: itemTree.itemList[0].name,
          securityContext: { roles: itemTree.extraRoles },
          session: this.convertItemToSession(itemTree, itemTree.itemList[0])
        }
      }
    );
  }

  convertItemToSession(itemTree: EditorBookingItemTree, item: EditorBookingItem) {
    const minimalSettings = EditorBookingItem
      .mapMinimalSettings(item.settings)
      .filter(setting => setting !== undefined);

    const minimalMetadataList = EditorBookingItem
      .filterMinimalMetadataList(item.metadataList);

    const session = {
      id: item.id,
      title: item.name,
      parts: minimalSettings.map(setting => setting.serializedName),
      sessions: item.children.map(childItemId =>
        this.convertItemToSession(itemTree, itemTree.getItemById(childItemId))
      ),
      ...Object.assign({}, ...minimalSettings.map(setting => {
        return { [setting.serializedName]: setting.getSerializedValues() };
      })),
      metadata: minimalMetadataList.map(metadata => ({ key: metadata.name, value: metadata.value }))
    };

    if (item.bundle.length > 0) {
      session.bundle = { options: item.bundle };
      session.parts.push('bundle');
    }

    if (item.children.length > 0) {
      session.allowBooking = item.userCanBook();
      session.hideTitles = item.hideSpecificBookingSettings;
    }

    return session;
  }
}
